<template>
  <div>
    <div
      style="position: static"
      :id="uniqueId"
      :style="rootStyle"
      v-if="
        lodash.isUndefined(render) || (!lodash.isUndefined(render) && render)
      ">
      <popper
        :boundingEleID="parentEleId"
        :customAttribs="{ show: listActive && !loading }"
        :disable="false"
        :hostEleID="`combobox-${uniqueId}`"
        :popoverID="`popover-${uniqueId}`"
        :tooltipMode="false"
        :warning="false"
        :placement="popoverPlacement"
        @emitError="$emit('emitError', err)">
        <div
          :class="{
            badvalue: showClasses && badValue && showErrorClass,
            combobox: showClasses,
            newvalue: showClasses && newValue && showWarningClass,
            goodvalue: showClasses && goodValue && showSuccessClass,
            novalue: (showClasses && badValue) || goodValue ? false : noValue,
          }"
          :id="`combobox-${uniqueId}`"
          :style="{
            height: heightOfInputBox,
            width: width ? `${width}rem` : 'auto',
          }">
          <div class="input-group">
            <span
              :id="`combobox-indicator-${inputId}`"
              :class="{
                'input-group-addon': showClasses,
                loading,
              }"
              v-show="showClasses">
              <template v-if="!loadingState">
                <font-awesome-icon
                  :icon="faIcons.faCircle"
                  v-if="
                    !((badValue && showErrorClass) || goodValue || newValue) ||
                    (!showSuccessClass && goodValue) ||
                    (newValue &&
                      !goodValue &&
                      noValue &&
                      !perfectMatch &&
                      !showWarningClass &&
                      !showErrorClass)
                  " />
                <font-awesome-icon
                  :icon="customWarningIcon || faIcons.faExclamationTriangle"
                  v-if="
                    ((newValue && !badValue) ||
                      (newValue && badValue && !showErrorClass)) &&
                    showWarningClass
                  " />
                <font-awesome-icon
                  :icon="customSuccessIcon || faIcons.faCheckCircleSolid"
                  v-if="goodValue && showSuccessClass" />
                <font-awesome-icon
                  :icon="customFailureIcon || faIcons.faTimesSolid"
                  v-if="badValue && showErrorClass" />
              </template>
              <loader-inline
                style="width: 1em"
                v-if="loadingState"
                :colorClass="loadingColorClass"></loader-inline>
            </span>
            <input
              :aria-label="placeholder"
              data-1p-ignore
              autocomplete="off"
              ref="comboInput"
              type="text"
              class="form-input"
              :class="{
                combobox: showClasses,
                disabled: disable,
                'no-border': inputNoBorder,
              }"
              :disabled="disable || null"
              :id="inputId"
              :placeholder="placeholder"
              :readonly="readOnly && !disable"
              :value="inputBoxValue"
              @blur="hideList"
              @click="$emit('clickInput')"
              @focus="showList"
              @keydown.down="selectNext"
              @keydown.enter="select"
              @keydown.tab="$emit('emitTab')"
              @keydown.up="selectPrev"
              @input="handleInput" />
            <transition name="fade">
              <button
                v-if="
                  clearable && internalValue && internalValue.name && !disable
                "
                class="clear"
                @click="clear(false, true)">
                <font-awesome-icon :icon="faIcons.faTimesSolid" />
                Clear
              </button>
            </transition>
          </div>

          <template v-if="embedList">
            <ul
              class="menu-combo-box alignment-list-item"
              :class="{
                above,
                'menu-position': !embedList,
                'menu-position-absolute': !embedList && !fixed,
                'menu-position-fixed': !embedList && fixed,
                'menu-position-embed': embedList,
              }"
              :style="{
                height: height > -1 ? `${height}vh` : 'auto',
                width: width ? `${width}rem` : 'auto',
              }"
              v-show="listActive && itemsFiltered.length > 0">
              <li
                class="menu-item"
                v-if="placeholderEmpty && filterName === ''"
                :style="listItemStyles">
                <em>{{ placeholderEmpty }}</em>
              </li>
              <template
                v-for="(item, idx) in itemsFiltered"
                :key="`${item[0].id}-${itemsKeyUpdateNonceValue}`">
                <!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
                <!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
                <li
                  class="menu-item"
                  v-if="
                    !placeholderEmpty || (placeholderEmpty && filterName !== '')
                  "
                  @mousedown.prevent
                  @click.prevent="selectItem(item)"
                  :key="
                    `combobox-drop-down-${item[0].id}-${idx}-` +
                    `${itemsKeyUpdateNonceValue}`
                  "
                  :ref="`item-${idx}`"
                  :class="{
                    selected:
                      (alwaysShowSelected || internalValue) &&
                      selectedIndexComp === idx,
                    disabled: item.length && itemsDisabled[item[0].id],
                    clickable:
                      item.length &&
                      itemsDisabled[item[0].id] &&
                      canSelectDisabledItems,
                  }"
                  :aria-disabled="itemsDisabled[item[0].id]"
                  :aria-selected="
                    (alwaysShowSelected || internalValue) &&
                    selectedIndexComp === idx
                  "
                  :style="listItemStyles">
                  <!-- esline-enable-->
                  <!-- esline-enable-->
                  <div class="tile tile-centered">
                    <div
                      v-if="itemColorBlock"
                      style="display: inline-block"
                      :style="itemColorBlock(item[0]._original)"></div>
                    <div
                      style="display: inline-block; margin-left: 0.7em"
                      v-if="itemIcon && itemIcon(item[0]._original)">
                      <font-awesome-icon
                        fixed-width
                        :icon="itemIcon(item[0]._original)" />
                    </div>
                    <div
                      v-if="useSafeHtml"
                      class="tile-content"
                      v-safe-html="item[1]"></div>
                    <div v-else class="tile-content" v-html="item[1]"></div>
                  </div>
                </li>
              </template>
              <template v-if="itemsFiltered.length === maxFilteredItems">
                <li class="menu-item disabled" :style="listItemStyles">
                  <div class="tile tile-centered">
                    <div class="tile-content"><em>And more...</em></div>
                  </div>
                </li>
              </template>
            </ul>
            <div
              @mousedown.prevent
              @click.prevent="$emit('emitClickEmptyListSlot')"
              @keydown.enter="$emit('emitClickEmptyListSlot')"
              @keydown.space="$emit('emitClickEmptyListSlot')"
              role="button"
              tabindex="0">
              <slot
                v-if="itemsFiltered.length === 0"
                name="empty-list-embed"></slot>
            </div>
          </template>
        </div>
        <template #popover>
          <ul
            class="menu-combo-box alignment-list-item"
            :id="`popover-${uniqueId}`"
            :class="{
              above,
              'menu-position': !embedList,
              'menu-position-absolute': !embedList && !fixed,
              'menu-position-fixed': !embedList && fixed,
              'menu-position-embed': embedList,
            }"
            :style="{
              height: height > -1 ? `${height}vh` : 'auto',
              width: width ? `${width}rem` : 'auto',
            }"
            v-show="!embedList && itemsFiltered.length > 0">
            <li class="menu-item" v-if="placeholderEmpty && filterName === ''">
              <em>{{ placeholderEmpty }}</em>
            </li>
            <template
              v-if="
                !placeholderEmpty || (placeholderEmpty && filterName !== '')
              ">
              <!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
              <!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
              <li
                class="menu-item"
                v-for="(item, idx) in itemsFiltered"
                @mousedown.prevent
                @click.prevent="selectItem(item)"
                :key="
                  `combobox-drop-down-${item[0].id}-${idx}` +
                  `${itemsKeyUpdateNonceValue}`
                "
                :ref="`item-${idx}`"
                :aria-selected="
                  (alwaysShowSelected || internalValue) &&
                  selectedIndexComp === idx
                "
                :class="{
                  selected:
                    (alwaysShowSelected || internalValue) &&
                    selectedIndexComp === idx,
                  disabled: item.length && itemsDisabled[item[0].id],
                  clickable:
                    item.length &&
                    itemsDisabled[item[0].id] &&
                    canSelectDisabledItems,
                }">
                <!-- eslint-enable -->
                <div
                  class="tile tile-centered"
                  :class="{ 'cb-list-item': useSafeHtml }">
                  <div
                    v-if="itemColorBlock"
                    style="display: inline-block"
                    :style="itemColorBlock(item[0]._original)"></div>
                  <div
                    style="display: inline-block; margin-left: 0.7em"
                    v-if="itemIcon && itemIcon(item[0]._original)">
                    <font-awesome-icon
                      fixed-width
                      :icon="itemIcon(item[0]._original)" />
                  </div>
                  <div
                    v-if="useSafeHtml"
                    class="tile-content cb-name"
                    v-safe-html="item[1]"></div>
                  <div
                    v-else
                    class="tile-content cb-name"
                    v-html="item[1]"></div>
                  <div class="cb-image" v-if="itemImage(item[0]._original)">
                    <lazy-image
                      v-if="itemImage(item[0]._original)"
                      :lazy-src="itemImage(item[0]._original)"
                      :height="40"
                      style="margin-left: 2em" />
                  </div>
                </div>
              </li>
              <template v-if="itemsFiltered.length === maxFilteredItems">
                <li
                  style="padding: 0.3em 1em; opacity: 0.7"
                  :style="listItemStyles">
                  <div class="tile tile-centered">
                    <div class="tile-content"><em>And more...</em></div>
                  </div>
                </li>
              </template>
            </template>
            <li
              v-if="
                lodash.get(this, 'autoCompRespData.length', 0) > 0 &&
                lodash.get(this, 'autoCompRespData.length', 0) <
                  autoCompNumItems
              "
              class="more-items-li">
              <span class="more-items">
                ...and {{ autoCompNumItems - autoCompRespData.length }} more
                {{ itemNameInComboBoxPlural }} not shown
              </span>
              <br />
              <span class="please-refine">
                Please refine your search selection to see these
                {{ itemNameInComboBoxPlural }}.
              </span>
            </li>
          </ul>
          <div
            v-if="!embedList"
            :class="{
              above,
              'menu-position-absolute': !embedList && !fixed,
              'menu-position-embed-no-scroll': embedList,
              'menu-position-fixed': !embedList && fixed,
              'menu-position-no-scroll': !embedList,
            }"
            @mousedown.prevent
            @click.prevent="$emit('emitClickEmptyListSlot')"
            @keydown.enter="$emit('emitClickEmptyListSlot')"
            @keydown.space="$emit('emitClickEmptyListSlot')"
            role="button"
            tabindex="0"
            :aria-label="emptyListPopoverName">
            <slot
              v-if="itemsFiltered.length === 0"
              name="empty-list-popover"></slot>
          </div>
        </template>
      </popper>

      <div :style="{ width: width ? `${width}rem` : 'auto' }">
        <span
          v-if="validatorMessage && validatorMessage.msg"
          style="float: right; font-size: 13px; text-align: right"
          :style="{
            color: validatorMsgColor,
          }">
          {{ validatorMessage.msg }}
        </span>
      </div>
    </div>
    <div v-show="usesOptionsSlot">
      <slot name="options"></slot>
    </div>
  </div>
</template>

<script>
/* This component uses @click events and also uses
 * @mousedown.prevent to prevent the default blur behavior
 * on mouse down so that click events still work.  Using click events are
 * important for WCAG 2.x A11y compliance.
 */
import _ from 'lodash';

import Debounce from 'assets/js/debounceasync';

import { Mutex } from 'async-mutex';

import { DangerColor, SuccessColor, WarningColor } from 'assets/js/colors';

import LazyImage from 'components/Widgets/LazyImage';
import LoaderInline from 'components/Widgets/LoaderInline';
import Popper from 'components/Widgets/Popper/Popper';

const debounceTime = 200;

const stripHTMLTags = (str) => {
  if (typeof str !== 'string') return '';
  return str.replace(/<[^>]*>?/gm, '');
};

// byName sorts a list of Object items by the name key.
const byName = (a, b) =>
  stripHTMLTags(String(a.name))
    .toLowerCase()
    .localeCompare(stripHTMLTags(String(b.name)).toLowerCase());

export default {
  name: 'combo-box',

  components: { LazyImage, LoaderInline, Popper },

  beforeUnmount() {
    if (this.dispenseGlobalProp) {
      this.dispenseGlobalProp.unsubscribe(this.uniqueIdLocal);
    }
  },

  computed: {
    // badValue returns whether or not the value in the text box is invalid.
    badValue() {
      if (this.validatorMessage && this.validatorMessage.typ === 'fail') {
        return true;
      }
      if (this.requiresValue && !this.internalValue) {
        return true;
      }
      if (
        this.requiresValue &&
        this.internalValue &&
        this.internalValue.id === 'none'
      ) {
        return true;
      }
      if (
        this.internalValue &&
        this.itemsDisabled[this.internalValue.id] === true
      ) {
        return true;
      }
      return (
        this.force &&
        ((!this.listActive && this.internalValue && !this.perfectMatch) ||
          this.itemsFiltered.length === 0)
      );
    },

    // goodValue computes whether or not the value for the text box was
    // previously set.
    goodValue() {
      if (this.goodValueOverride) return true;
      if (this.badValue) return false;
      if (this.validatorMessage && this.validatorMessage.typ === 'success') {
        return true;
      }
      return (
        this.internalValue &&
        this.perfectMatch &&
        this.internalValue.id !== 'none' &&
        this.itemsDisabled[this.internalValue.id] !== true
      );
    },

    // itemsFiltered returns a filtered list of the items in itemList
    // based on the filter string input by the user. It has templating
    // decoration to make the boxes appear neater.
    itemsFiltered() {
      if (
        this.internalValue &&
        this.internalValue.name &&
        _.isString(this.internalValue.name) &&
        this.internalValue.name.trim().length > 0 &&
        this.internalValue.id !== 'none' &&
        this.internalValue.name !== '' &&
        this.internalValue.id !== undefined
      ) {
        // Add a nice looking note if we have a function for it.
        if (this.displayAllWhenSelected) {
          const names = this.nameMapper(this.itemsParsed);
          return _.zip(this.itemsParsed, names);
        }
        const filteredIds = _.filter(this.itemsParsed, (val) =>
          val.id.match(this.internalValue.id),
        ).slice(0, this.maxFilteredItems);

        const highlighted = this.nameHighlightMapper(filteredIds);
        const zipped = _.zip(filteredIds, highlighted);

        return _.sortBy(zipped, [(item) => item[1].indexOf('<mark>')]);
      }
      if (
        this.internalValue &&
        this.internalValue.name &&
        _.isString(this.internalValue.name) &&
        this.internalValue.name.trim().length > 0 &&
        (this.internalValue.id === 'none' ||
          this.internalValue.id === '' ||
          this.internalValue.id === undefined)
      ) {
        let str = String(this.internalValue.name).toLowerCase();
        if (!str) str = '';
        const filteredByNames = _.filter(this.itemsParsed, (item) => {
          if (
            stripHTMLTags(String(item.name)).toLowerCase().indexOf(str) !== -1
          ) {
            return true;
          }
          // Check any original item fields.
          let matched = false;
          if (
            this.fieldsToMatch &&
            this.fieldsToMatch.length > 0 &&
            item['_original']
          ) {
            _.forEach(this.fieldsToMatch, (field) => {
              matched = matched || item['_original'][field] === str;
            });
          }
          return matched;
        }).slice(0, this.maxFilteredItems);
        const names = this.nameMapper(filteredByNames);
        return _.zip(filteredByNames, names);
      }

      const names = this.nameMapper(
        this.itemsParsed.slice(0, this.maxFilteredItems),
      );
      return _.zip(this.itemsParsed.slice(0, this.maxFilteredItems), names);
    },

    loadingColorClass() {
      if (this.badValue) return 'danger';
      if (this.noValue) return 'danger';
      if (this.goodValue) return 'success';
      if (this.newValue) return 'warning';
      return '';
    },

    // newValue computes whether or not the value for the text box is newly
    // set.
    newValue() {
      if (this.validatorMessage && this.validatorMessage.typ === 'warn') {
        return true;
      }
      return (
        !this.force &&
        this.internalValue &&
        _.isString(this.internalValue.name) &&
        this.internalValue.name &&
        !this.perfectMatch
      );
    },

    // noValue computers whether or not the text box is missing a value.
    noValue() {
      if (
        !this.newValue &&
        !this.badValue &&
        !this.goodValue &&
        !this.perfectMatch
      ) {
        return true;
      }

      if (!this.perfectMatch && !this.showErrorClass) return true;

      return false;
    },

    // perfectMatch returns true if the selection of the text box matches
    // the id of some value in the passed list of options.
    perfectMatch() {
      if (this.validatorMessage && this.validatorMessage.typ === 'success') {
        return true;
      }
      if (
        this.internalValue &&
        this.internalValue.name &&
        _.isString(this.internalValue.name) &&
        this.internalValue.name.trim().length > 0 &&
        this.internalValue.id !== 'none'
      ) {
        if (this.itemsAll && this.itemsAll.length > 0) {
          return _.includes(
            _.map(this.itemsAll, (f) => this.itemId(f)),
            this.internalValue.id,
          );
        }

        return _.includes(
          _.map(this.itemsParsed, (f) => f.id),
          this.internalValue.id,
        );
      }

      return false;
    },

    // selectedIndexComp is the computed selected index.
    selectedIndexComp() {
      if (this.selectedIndex > -1) return this.selectedIndex;
      if (
        this.itemsFiltered.length > 0 &&
        this.internalValue &&
        this.internalValue.id !== 'none'
      ) {
        return this.itemsFiltered.findIndex(
          (itm) => itm[0].id === this.internalValue.id,
        );
      }
      if (
        this.autoCompRespData.length > 0 &&
        this.internalValue &&
        this.internalValue.id !== 'none'
      ) {
        return this.autoCompRespData.findIndex(
          (itm) => this.itemId(itm) === this.internalValue.id,
        );
      }
      return -1;
    },

    // usesOptionsSlot returns true if the parent has the options slot declared.
    usesOptionsSlot() {
      return !!_.get(this, '$slots.options');
    },

    // validatorMsgColor returns the current colour of the validator message.
    validatorMsgColor() {
      if (!this.validatorMessage) return '#000000';
      if (this.validatorMessage.typ === 'fail') return DangerColor;
      if (this.validatorMessage.typ === 'success') return SuccessColor;
      if (this.validatorMessage.typ === 'warn') return WarningColor;
      return '#000000';
    },
  },

  created() {
    this.uniqueId = this.uuid4();
    if (this.dispenseGlobalProp) {
      this.uniqueIdLocal = this.name;
      if (this.uniqueIdDispenser) {
        this.uniqueIdLocal = this.uniqueIdDispenser;
      }

      this.dispenseGlobalProp.subscribe(this.uniqueIdLocal, async (val) => {
        switch (val.name) {
          case 'clear': {
            await this.clear(true);
            return null;
          }
          case 'clearinputtext': {
            this.inputBoxValue = '';
            return null;
          }
          case 'focus': {
            this.$refs.comboInput.focus();
            return null;
          }
          case 'hasitem': {
            return Boolean(
              _.find(
                this.itemsParsed,
                (itm) => itm.id === _.get(val, 'value', ''),
              ),
            );
          }
          case 'hidelist': {
            this.hideList();
            return null;
          }
          case 'itemset': {
            this.inputBoxValue = stripHTMLTags(_.get(val, 'value', ''));
            await this.handleItemSet(val.value);
            return null;
          }
          case 'itemsetnoemit': {
            await this.handleItemSet(val.value, true);
            return null;
          }
          case 'refetch': {
            // val.value is forceRefreshByAutocomplete.
            this.itemsParsed = await this.itemsFetch(val.value);
            return null;
          }
          default: {
            console.warn(
              'unknown message from parent component: ' +
                `${JSON.stringify(val)}`,
            );
            return null;
          }
        }
      });
    }
  },

  data() {
    return {
      autoCompDebounced: Debounce(this.autocomplete, this.debounceTime),
      autoCompNumItems: -1,
      autoCompRespData: [],
      autoCompSearch: '',
      filterName: this.value ? this.value.name : '',
      inputBoxValue: '',
      inputMutex: new Mutex(),
      internalValue: this.value,
      itemsKeyUpdateNonceValue: 0,
      itemsParsed: [],
      listActive: false,
      loading: false,
      selectedIndex: this.initialSelectedIndex,
      stripHTMLTags,
      uniqueId: null,
      validatorMessage: null,
    };
  },

  emits: [
    'cleared',
    'clickInput',
    'emitEnter',
    'emitTab',
    'emitClickEmptyListSlot',
    'emitError',
    'input',
    'validate',
    'listActive',
  ],

  methods: {
    // clear clears the text box of inputs.
    async clear(noFocus, fromUserClearClick) {
      this.filterName = '';
      this.autoCompSearch = '';
      this.internalValue = { name: '', id: 'none' };
      try {
        this.itemsParsed = await this.itemsFetch();
      } catch (err) {
        this.$emit('emitError', err);
      }
      this.validatorMessage = null;
      this.$emit('input', { name: '', id: 'none' });
      this.$emit('validate', null);
      if (this.clearShowList && !noFocus) {
        this.showList();
      }
      if (!noFocus && this.clearShowList) {
        this.$refs.comboInput.focus();
      }
      this.selectedIndex = -1;

      // Only emit the cleared signal if the button was pressed.
      if (fromUserClearClick) {
        this.$emit('cleared');
        this.inputBoxValue = '';
      }
    },

    handleInput($event) {
      this.inputBoxValue = $event.target.value;
      this.updateFilter($event.target.value);
      this.showList();
    },

    // handleItemSet handles a dispenser request for setting an item with
    // a given ID.
    async handleItemSet(item, noEmit) {
      const lockRelease = await this.inputMutex.acquire();

      if (!item && _.get(this, 'internalValue.id') !== 'none') {
        await this.clear(true);
        lockRelease();
        return;
      }

      if (this.items && this.items.length) {
        this.selectedIndex = _.findIndex(
          this.items,
          (i) => this.itemId(i) === this.itemId(item),
        );
      }
      if (!this.items && this.itemsParsed && this.itemsParsed.length) {
        this.selectedIndex = _.findIndex(
          this.itemsParsed,
          (i) => i.id === this.itemId(item),
        );
      }

      this.internalValue = {
        name: this.itemName(item),
        id: this.itemId(item),
      };
      if (this.validator) await this.validate();
      if (!noEmit) this.$emit('input', item);
      lockRelease();
    },

    // itemsFetch fetches the list of items to display to the user and iterate
    // through. It is automatically sorted by name and generated by the
    // passed functions for name and ID.
    async itemsFetch(forceRefreshByAutocomplete) {
      if (
        this.items &&
        this.items.length > 0 &&
        !(forceRefreshByAutocomplete && this.autocomplete)
      ) {
        const parsed = this.parseItems(this.items);
        return parsed.sort(this.sortByFn);
      }

      if (
        this.perfectMatch &&
        this.autoCompRespData.length > 0 &&
        !forceRefreshByAutocomplete
      ) {
        const parsed = this.parseItems(this.autoCompRespData);
        return parsed.sort(this.sortByFn);
      }

      if (this.autocomplete && (!this.items || forceRefreshByAutocomplete)) {
        this.loading = true;
        let resp;
        try {
          resp = await this.autoCompDebounced(
            this.autoCompSearch ? this.autoCompSearch : '',
          );

          // This works with raw responses or arrays returned from the
          // function.
          if (_.isPlainObject(resp)) {
            this.autoCompRespData = resp.data;
            this.autoCompNumItems = resp.numitems;
          } else {
            this.autoCompRespData = resp;
            this.autoCompNumItems = resp.length;
          }
        } catch (err) {
          this.$emit('emitError', err);
        }

        this.loading = false;

        if (resp && !_.isPlainObject(resp)) {
          const parsed = this.parseItems(resp);
          return parsed.sort(this.sortByFn);
        }
        if (resp && _.isPlainObject(resp)) {
          const parsed = this.parseItems(resp.data);
          return parsed.sort(this.sortByFn);
        }
      }

      return [];
    },

    // itemsFetchAndAssign fetches the list of items and assigns it to
    // itemsParsed. Used for async calling of fetch of list items.
    async itemsFetchAndAssign() {
      try {
        this.itemsParsed = await this.itemsFetch();
      } catch (err) {
        this.$emit('emitError', err);
      }
    },

    // parseItems parses items into a list of names and ids, with the original
    // object generated from it in "_original".
    parseItems(items) {
      const parsed = _.map(items, (item) => {
        const parsedItem = {
          _original: item,
          name: this.itemName(item),
          id: this.itemId(item),
        };
        return parsedItem;
      });

      return parsed;
    },

    // select selects the current highlighted item when a user keys down
    // 'enter'.
    async select() {
      this.$emit('emitEnter');
      if (
        this.selectedIndex >= 0 &&
        this.selectedIndex <= this.itemsFiltered.length - 1
      ) {
        const val = this.itemsFiltered[this.selectedIndex][0];
        if (this.itemsDisabled[val.id]) return;

        const lockRelease = await this.inputMutex.acquire();
        this.internalValue = val;
        let items = this.items;
        if (!items && this.autocomplete) {
          items = this.autoCompRespData;
        }
        const originalItem = _.find(items, (i) => this.itemId(i) === val.id);
        if (this.validator) {
          await this.validate(
            stripHTMLTags(this.internalValue.name),
            originalItem,
          );
        }
        this.$emit('input', originalItem);
        // This used to not run with embedList.
        this.hideList();
        if (this.clearOnSelect) this.clear(true);
        lockRelease();
      }
    },

    // selectItem selects an item with its respective ID, then emits an
    // input item.
    async selectItem(item) {
      if (this.itemsDisabled[item[0].id] && !this.canSelectDisabledItems) {
        return;
      }
      const lockRelease = await this.inputMutex.acquire();
      this.internalValue = item[0];
      let items = this.items;
      if (!items && this.autocomplete) {
        items = this.autoCompRespData;
      }
      const originalItem = _.find(items, (i) => this.itemId(i) === item[0].id);
      if (this.validator) {
        await this.validate(
          stripHTMLTags(this.internalValue.name),
          originalItem,
        );
      }
      this.$emit('input', originalItem);
      // This used to not run with embedList.
      this.hideList();
      if (this.clearOnSelect) this.clear(true);
      lockRelease();
    },

    // selectNext selects the next item when the user keys down 'down'.
    selectNext() {
      this.selectedIndex += 1;
      if (this.selectedIndex >= this.itemsFiltered.length) {
        this.selectedIndex = this.itemsFiltered.length - 1;
      }
      const refName = `item-${this.selectedIndex}`;
      this.$refs[refName]?.[0]?.scrollIntoView();
    },

    // selectPrev selects the previous item when the user keys down 'up'.
    selectPrev() {
      this.selectedIndex -= 1;
      if (this.selectedIndex < 0) this.selectedIndex = 0;
      const refName = `item-${this.selectedIndex}`;
      this.$refs[refName]?.[0]?.scrollIntoView();
    },

    // updateFilter sets the internal value of the filter/textbox and emits
    // an input event. Importantly, the constant ID 'none' is emitted,
    // indicating to the parent that the user has yet to make a selection.
    async updateFilter(val) {
      const lockRelease = await this.inputMutex.acquire();
      this.filterName = val;
      if (this.autocomplete && !this.items) this.autoCompSearch = val;
      this.internalValue = { name: val, id: 'none' };
      this.$emit('input', this.internalValue);
      if (this.validator) await this.validate(val);
      this.itemsFetchAndAssign();
      lockRelease();
    },

    // showList shows the list of items.
    showList: function showList() {
      if (this.disable) return;
      this.listActive = true;
      this.selectedIndex = -1;
      if (this.selectedIndexComp > -1) {
        this.selectedIndex = this.selectedIndexComp;
      }
    },

    // hideList hides the list of items.
    hideList: function hideList() {
      const inputEle = document.getElementById(this.inputId);
      if (inputEle) inputEle.blur();
      this.listActive = false;
      this.selectedIndex = -1;
    },

    // validate validates the input or user selection according to the
    // validate function passed by the user. It does nothing if validator
    // is unset.
    async validate(val) {
      if (this.validator) {
        let resp;
        try {
          resp = await this.validator(val, this.internalValue);
        } catch (err) {
          this.$emit('emitError', err);
        }

        if (resp) {
          this.validatorMessage = resp;
          this.$emit('validate', resp);
        }
      }
    },
  },

  async mounted() {
    const lockRelease = await this.inputMutex.acquire();

    let fetched;
    try {
      fetched = await this.itemsFetch();
    } catch (err) {
      this.$emit('emitError', err);
    }

    this.itemsParsed = fetched;
    if (this.defaultId && this.upstreamItem) {
      throw Error('default id and upstream item both set, pick one');
    }
    if (this.uniqueIdDispenser && !_.isUndefined(this.upstreamItem)) {
      throw Error('dispenser id and upstream item both set, pick one');
    }
    if (this.defaultId && !this.upstreamItem) {
      let items = this.items;
      if ((!items || items.length === 0) && this.autocomplete) {
        items = this.autoCompRespData;
      }
      const defaultItemIdx = _.findIndex(
        items,
        (item) => this.defaultId === this.itemId(item),
      );
      const defaultItem = items[defaultItemIdx];
      this.selectedIndex = defaultItemIdx;
      this.internalValue = {
        name: this.itemName(defaultItem),
        id: this.itemId(defaultItem),
      };
    }

    if (this.$refs.comboInput && !this.dontFocus) {
      // This sleep is to deal with a bug with popper where on load with
      // focus the menu appears way across the screen. 200 ms is required
      // for Chrome, while 300 ms is required for Firefox. Attempts to trace
      // why this happens were unsuccessful. Even waiting for popper to load,
      // hiding the menu, and then displaying the menu don't work. There seems
      // to be some kind of bug with initial alignment in popper.
      if (this.spectreAnimationBugFixOnFocus) await this.sleep(300);
      this.$refs.comboInput.focus();
    }

    lockRelease();

    if (_.isFunction(this.onInit)) this.onInit();
  },

  props: {
    // Arrays
    fieldsToMatch: {
      type: Array,
      required: false,
      default: () => [],
    },
    items: {
      type: Array,
      required: false,
      default: () => null,
    },
    // itemsAll is used for when a list is dynamically resizing based on the
    // selection of itself or other lists, and we need to prune out some
    // values. It ensures that perfect match remains set even when the
    // original items are missing from items.
    itemsAll: {
      type: Array,
      required: false,
      default: () => null,
    },

    // Booleans

    // above displays the dropdown as being above, rather than below, the
    // input box. Deprecated when combobox was moved to popper.js.
    // TODO remove?
    above: { type: Boolean, default: false },

    // alwaysShowSelected shows the selected cursor even if there is no
    // currently assigned value in the combobox.
    alwaysShowSelected: { type: Boolean, default: false },

    // canSelectDisabledItems styles disabled items as disabled but lets you
    // select them anyway. Can be useful to show that an item isn't available
    // but you still want to do something when it is selected (e.g. show a
    // helpful message to users with more details).
    canSelectDisabledItems: { type: Boolean, default: false },

    // clearable toggles whether or not to show the clear button in the
    // combobox.
    clearable: { type: Boolean, default: true },

    // clearOnSelect toggles whether or not to show the box will clear on
    // selection.
    clearOnSelect: { type: Boolean, default: false },

    // clearShowList toggles whether or not to show the items list after
    // clearing the combobox.
    clearShowList: { type: Boolean, default: true },

    // disable disables the combobox and leaves only the set value within
    // it.
    disable: { type: Boolean, default: false },

    // displayAllWhenSelected toggles filtering of items in text box based
    // on what the user enters into the textbox.
    displayAllWhenSelected: { type: Boolean, default: true },

    // dontFocus toggles focusing the text box on load.
    dontFocus: { type: Boolean, default: true },

    // embedList embeds the list below the combobox. Typically used when
    // switching to mobile mode.
    embedList: { type: Boolean, default: false },

    // fixed uses fixed instead of absolute positioning to place the
    // items list.
    fixed: { type: Boolean, default: false },

    // force forces the user to select an item from the list. If an item
    // is not from the list, the box display its warning style.
    force: { type: Boolean, default: false },

    // goodValueOverride lets you override the good value button, allowing
    // you to force a good value.
    goodValueOverride: { type: Boolean, default: false },

    // inputNoBorder causes no border to show up on the input box.
    inputNoBorder: { type: Boolean, default: false },

    // itemsKeyUpdateNonce enables a nonce for item keys in the list of
    // items so that when the deep watcher on items updates, it forces a
    // refresh of the menu contents of the items. This is necessary for cases
    // where things like the name of the items may be dynamically updated.
    itemsKeyUpdateNonce: { type: Boolean, default: false },

    // listActiveEmitIncludeState causes listActive emit events to include
    // an object detailing the component state the moment it is called.
    listActiveEmitIncludeState: { type: Boolean, default: false },

    // loadingState shows the class icon as loading when true overriding
    // any internal state. Meant to mean that it is part of a larger loading
    // state.
    loadingState: { type: Boolean, default: false },

    // readOnly sets the input box to be read-only.
    readOnly: { type: Boolean, default: false },

    // render disables or enabled rendering of the virtual node. Useful
    // for optimisations where you mess around with the internal combobox
    // data before rending.
    render: { default: undefined },

    // requiresValue is similar to force.
    // TODO Should this be deprecated? I'm having trouble figuring out
    // the difference with force, but it might have something to do with
    // input box validation.

    // NOTE FROM BRANDON JUNE 28 2019:
    // Force requires a value from the list, requires value just means
    // we require SOME value (from list or not).
    requiresValue: { type: Boolean, default: false },

    // showClasses toggles whether or not to show any of the default classes.
    showClasses: { type: Boolean, default: true },

    // showErrorClass toggles whether or not the error class is shown in
    // the combobox.
    showErrorClass: { type: Boolean, default: true },

    // showSuccessClass toggles whether or not the success class is shown in
    // the combobox.
    showSuccessClass: { type: Boolean, default: true },

    // showWarningClass toggles whether or not the warning class is shown in
    // the combobox.
    showWarningClass: { type: Boolean, default: true },

    // Fixes popper not locating the element location correctly because
    // spectre in animating modal insertion.
    spectreAnimationBugFixOnFocus: { type: Boolean, default: false },

    // toggleFocus is a boolean that toggles the focus state when its state
    // is flipped.
    toggleFocus: { type: Boolean },

    // useSafeHtml indicates whether or not the name should feed in HTML as
    // sanitized HTML or not. If set to false, you MUST ensure the sanity of
    // the HTML coming in.
    useSafeHtml: { type: Boolean, default: true },

    // Functions
    // autocomplete should be an asynchronous fetcher that returns a
    // list that is parseable to id and name.
    autocomplete: {
      type: Function,
      required: false,
    },
    itemId: {
      type: Function,
      required: true,
    },
    itemImage: {
      type: Function,
      required: false,
      default: () => false,
    },
    itemName: {
      type: Function,
      required: true,
    },
    itemColorBlock: {
      type: Function,
      required: false,
    },
    itemIcon: {
      type: Function,
      required: false,
    },
    nameMapper: {
      type: Function,
      required: false,
      default: (itemsParsed) =>
        _.map(itemsParsed, (item) => stripHTMLTags(item.name)),
    },
    nameHighlightMapper: {
      type: Function,
      required: false,
      default: (filteredIds) =>
        _.map(filteredIds, (item) =>
          item.name.replace(
            item.name,
            `<mark>&nbsp;${item.name}&nbsp;</mark>&nbsp;&nbsp;`,
          ),
        ),
    },
    onInit: {
      type: Function,
      required: false,
    },
    sortByFn: {
      type: Function,
      required: false,
      default: byName,
    },

    // validator should be an asynchronous function that determines the
    // validity of a user entered input and the components internalValue
    // and then returns the following output:
    // {
    //   msg: (String) string to display to the user about their output
    //   typ: (String) type of message, valid options are 'fail', 'warn',
    //     and 'success'
    //  }
    //
    // The validation message will also override the default classes for
    // the box, causing it to align with the respective message.
    validator: {
      // type: Function, disabled because there are no untruthy functions
      // in javascript.
      required: false,
    },

    // Numbers
    debounceTime: {
      type: Number,
      required: false,
      default: debounceTime,
    },
    height: {
      type: Number,
      required: false,
      default: -1,
    },

    initialSelectedIndex: {
      type: Number,
      required: false,
      default: -1,
    },

    // maxFilteredItems is the maximum number of filtered items to display.
    maxFilteredItems: {
      type: Number,
      default: 999,
      required: false,
    },

    width: {
      type: Number,
      required: false,
    },

    // Objects
    // itemsDisabled is for greying out list items by id.
    itemsDisabled: {
      type: Object,
      default: () => ({}),
    },
    listItemStyles: {
      type: Object,
      default: () => ({}),
    },
    rootStyle: {
      type: Object,
      default: () => ({}),
    },
    upstreamItem: {},

    // Strings
    customFailureIcon: {
      type: Object,
    },
    customSuccessIcon: {
      type: Object,
    },
    customWarningIcon: {
      type: Object,
    },
    defaultId: {
      type: [String, Number],
    },
    emptyListPopoverName: {
      type: String,
      required: false,
      default: 'No results',
    },
    heightOfInputBox: {
      type: String,
      required: false,
      default: 'auto',
    },
    inputId: {
      type: String,
      default: 'combobox-input-id',
    },
    itemNameInComboBox: {
      type: String,
      default: 'item',
    },
    itemNameInComboBoxPlural: {
      type: String,
      default: 'items',
    },
    parentEleId: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
    },
    placeholderEmpty: {
      type: String,
      default: '',
    },
    popoverPlacement: {
      type: String,
      default: 'bottom-start',
    },
    // uniqueIdDispenser is the ID for use in handling dispenser calls.
    uniqueIdDispenser: {
      type: String,
      required: false,
    },
  },

  watch: {
    autocomplete: {
      handler: async function handle(val) {
        if (val) {
          this.autoCompDebounced = Debounce(val, this.debounceTime);
          let fetched;
          try {
            fetched = await this.itemsFetch(true);
          } catch (err) {
            this.$emit('emitError', err);
            return;
          }

          this.itemsParsed = fetched;
        }
      },
    },
    internalValue: {
      handler: function handle(val) {
        if (!val) return;
        if (val && val.id === 'none') return;
        this.inputBoxValue = this.stripHTMLTags(_.get(val, 'name', ''));
      },
      deep: true,
    },
    items: {
      handler: async function handle() {
        this.itemsKeyUpdateNonceValue += 1;
        if (_.isFunction(this.autocomplete) && !_.isArray(this.items)) return;
        let fetched;
        try {
          fetched = await this.itemsFetch();
        } catch (err) {
          this.$emit('emitError', err);
          return;
        }

        this.itemsParsed = fetched;

        if (
          this.itemsKeyUpdateNonce &&
          _.isPlainObject(this.internalValue) &&
          this.internalValue.id !== 'none'
        ) {
          const itm = _.find(
            this.itemsParsed,
            (_itm) => _itm.id === this.internalValue.id,
          );
          if (itm) {
            this.internalValue.name = itm.name;
            this.internalValue.id = itm.id;
            this.inputBoxValue = this.stripHTMLTags(itm.name);
          }
        }
      },
      deep: true,
    },
    listActive: {
      handler: async function handle(val) {
        // scroll into view if active with selectedItem
        await this.$nextTick();
        const activeItemEl = this.$refs[`item-${this.selectedIndexComp}`];
        if (val && activeItemEl) {
          const [el] = activeItemEl;
          // Since the popper library is external to vue, nextTick
          // above isnt enough and we need to do a classic JS hack since
          // I'm assuming its handling the DOM as well.
          setTimeout(() => el.scrollIntoView(), 100);
        }

        if (this.listActiveEmitIncludeState) {
          this.$emit('listActive', {
            inputBoxValue: this.inputBoxValue,
            internalValue: this.internalValue,
            itemsFiltered: this.itemsFiltered,
            listActive: val,
            selectedIndex: this.selectedIndex,
          });
          return;
        }
        this.$emit('listActive', val);
      },
    },
    selectedIndex: {
      handler: async function handle(idx) {
        if (idx === -1) return;
        if (!this.itemsFiltered) return;
        const item = this.itemsFiltered[idx];
        if (!this.item) return;

        const lockRelease = await this.inputMutex.acquire();
        this.internalValue = item[0];
        let actualItem;
        if (this.items && this.items.length > 0) {
          actualItem = _.find(
            this.items,
            (itm) => this.itemId(itm) === this.internalValue.id,
          );
        }
        if (this.autoCompRespData.length > 0) {
          actualItem = _.find(
            this.autoCompRespData,
            (itm) => this.itemId(itm) === this.internalValue.id,
          );
        }
        if (this.validator) await this.validate();
        this.$emit('input', actualItem);
        lockRelease();
      },
    },
    toggleFocus: {
      handler: function handle() {
        if (this.$refs?.comboInput) {
          this.$refs.comboInput.focus();
        }
      },
    },
    upstreamItem: {
      handler: async function handle(item) {
        if (!item && _.get(this, 'internalValue.id') !== 'none') {
          await this.clear(true);
          return;
        }

        const lockRelease = await this.inputMutex.acquire();

        this.selectedIndex = -1;
        this.internalValue = {
          name: this.itemName(item),
          id: this.itemId(item),
        };
        if (this.validator) await this.validate();
        this.$emit('input', item);
        lockRelease();
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'sass:color';
@import '~assets/css/colors';

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.1s ease-in;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

button.clear {
  font-size: x-small;
  background-color: #767676;
  color: #fff;
  border: none;
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  cursor: pointer;
}

.input-group {
  position: relative;
}

div.combobox.goodvalue {
  input.combobox {
    /* Allow dark mode success color override. */
    border: 1px solid var(--dm-success-color, $successcolor);
    color: var(--dm-success-color, $successcolor);
  }
}
div.combobox.goodvalue {
  span.input-group-addon {
    /* Allow dark mode success color override. */
    border: 1px solid var(--dm-success-color, $successcolor);
    color: $successcolor; /* Keep this core success color. */
    background-color: #efe;
  }
}
div.combobox.newvalue {
  input.combobox {
    border: 1px solid $warningcolor;
    color: color.adjust($warningcolor, $lightness: -10%);
    font-weight: bold;
  }
  span.input-group-addon {
    border: 1px solid $warningcolor;
    color: color.adjust($warningcolor, $lightness: -10%);
    background-color: #fff4db;
  }
}
div.combobox.badvalue {
  input.combobox {
    border: 1px solid $dangercolor;
    color: $dangercolor;
  }
  span.input-group-addon {
    border: 1px solid $dangercolor;
    color: $dangercolor;
    background-color: #ffe8db;
  }
}

.menu-combo-box {
  background: #fff;
  border-radius: 0.2rem;
  box-shadow: 0 0.2rem 0.5rem rgba(69, 77, 93, 0.3);
  list-style: none;
  margin: 0;
  min-width: 18rem;
  margin-top: 6px;
  padding: 1rem 0;
  z-index: 1000;
}

.dark-mode {
  .menu-combo-box {
    background: $dmbg;
    border: 1px solid $dmbglight;
  }
  .menu-item {
    &:hover {
      background: $dmbglight !important;
    }
    strong {
      background-color: rgb(55, 55, 12);
    }
    &.selected {
      background: $dmbglight !important;
    }
    &.disabled {
      color: $dangercolor;
      background-color: color.adjust($dangercolor, $lightness: 40%) !important;
    }
    &.disabled:hover {
      color: $dangercolor;
      background-color: color.adjust($dangercolor, $lightness: 40%) !important;
    }
  }
}

.menu-item:hover {
  background-color: var(
    --primary-color-lighten-70-pale,
    $primarycolorlighten70pale
  ) !important;
}

.menu-item {
  cursor: pointer;
  margin-top: 0;
  padding: 0.5rem 1rem;

  /* Padding in Spectre comes from the <a> tag in menu items
                  which we don't use. */
  .tile {
    padding: 0.6rem 0.8rem;
    margin: 0 -0.8rem;
  }

  strong {
    background-color: rgb(255, 255, 200);
  }
  &.selected {
    background-color: var(
      --primary-color-lighten-70-pale,
      $primarycolorlighten70pale
    ) !important;
  }
  &.disabled {
    color: $dangercolor;
    background-color: color.adjust($dangercolor, $lightness: 40%);
    cursor: not-allowed;
  }
  &.disabled:hover {
    color: $dangercolor;
    background-color: color.adjust($dangercolor, $lightness: 40%) !important;
    cursor: not-allowed;
  }
  &.disabled.clickable:hover {
    cursor: pointer;
  }
}

.menu-position {
  overflow-y: scroll;
  max-height: 20em;
}

.menu-position-no-scroll {
  max-height: 20em;
}

.menu-position-absolute {
  position: absolute;
  z-index: 500;
}

.menu-position-fixed {
  isolation: isolate;
  position: fixed;
  z-index: 3;
}

.menu-position-embed {
  overflow-y: scroll;
  position: relative;
  margin-bottom: 8px;
}

.menu-position-embed-no-scroll {
  position: relative;
  margin-bottom: 8px;
}

.above {
  height: 50rem;
  margin-top: -318px;
}

.alignment-list-item {
  text-align: left;
}
.form-input.disabled,
.form-input:disabled,
.form-select.disabled,
.form-select:disabled {
  opacity: 1;
  background-color: #0303030d;
}

.more-items-li {
  padding-left: 1em;
  padding-right: 1em;
}

.more-items {
  font-size: 12px;
  font-weight: 600;
}

.please-refine {
  font-size: 12px;
  font-style: italic;
}

input.no-border {
  border: 0;
}

.sz-11-italic {
  font-size: 11px;
  font-style: italic;
}

.cb-list-item {
  display: flex;
}

.cb-list-item {
  .cb-name,
  .cb-image {
    display: flex;
    flex-direction: column;
  }
  .cb-name {
    flex-grow: 1;
  }
}

.form-input[readonly] {
  background: #fff;
  cursor: pointer;
}

.dark-mode {
  .form-input[readonly] {
    background: $dmbg;
  }
}
</style>
